import { useTranslation } from "@next-platform/client-common-core";
import type RulebookAttachTarget from "@next-platform/client-common-core/rulebook/attach/RulebookAttachTarget";
import { Button, ButtonVariant, ListEmpty, Spinner, Text, TextVariants } from "@next-platform/client-common-spicy-ui";

import Trans from "@/components/Trans/Trans";

import RulebookAttachLoadMore from "./RulebookAttachLoadMore";
import RulebookAttachTargetList from "./RulebookAttachTargetList";
import type { RulebookAttachOwnerKind } from "./useRulebookAttachTargets";

export type RulebookAttachTargetContentProps = {
  ownerKind: RulebookAttachOwnerKind;
  rulebookId: string;
  search: string;
  targets: RulebookAttachTarget[];
  selectedId: string | null;
  isAttaching: boolean;
  isLoading: boolean;
  isInitialError: boolean;
  isLoadMoreError: boolean;
  hasMoreCharacters: boolean;
  isLoadingMoreCharacters: boolean;
  onLoadMore: () => void;
  onSelectTarget: (id: string) => void;
  onRetry: () => void;
};

const RulebookAttachTargetContent = ({
  ownerKind,
  rulebookId,
  search,
  targets,
  selectedId,
  isAttaching,
  isLoading,
  isInitialError,
  isLoadMoreError,
  hasMoreCharacters,
  isLoadingMoreCharacters,
  onLoadMore,
  onSelectTarget,
  onRetry,
}: RulebookAttachTargetContentProps) => {
  const { t } = useTranslation();

  return (
    <>
      {isLoading ? (
        <div className="flex min-h-[160px] items-center justify-center">
          <Spinner className="mx-auto" />
        </div>
      ) : null}
      {isInitialError && !isLoading ? (
        <div className="flex min-h-[160px] flex-col items-center justify-center gap-sm" data-testid="RulebookAttachTargetContent-Error">
          <Text variant={TextVariants.LABEL_MEDIUM_BOLD}>
            <Trans i18nKey="rulebook:attach.error.title" />
          </Text>
          <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-10">
            <Trans i18nKey="rulebook:attach.error.description" />
          </Text>
          <Button variant={ButtonVariant.OUTLINED} type="button" onClick={onRetry} data-testid="RulebookAttachTargetContent-RetryButton">
            <Trans i18nKey="rulebook:attach.error.retry" />
          </Button>
        </div>
      ) : null}
      {!isLoading && !isInitialError && !targets.length ? (
        <div className="flex min-h-[160px] items-center justify-center">
          <ListEmpty
            isLoading={false}
            title={t("rulebook:attach.empty.title")}
            description={search.trim().length > 0 ? t("rulebook:attach.empty.searchDescription") : t("rulebook:attach.empty.description")}
          />
        </div>
      ) : null}
      {!isLoading && targets.length ? (
        <RulebookAttachTargetList
          targets={targets}
          rulebookId={rulebookId}
          selectedId={selectedId}
          isAttaching={isAttaching}
          onSelectTarget={onSelectTarget}
        />
      ) : null}
      {isLoadMoreError ? (
        <div className="flex items-center justify-center gap-sm pt-sm" data-testid="RulebookAttachTargetContent-LoadMoreError">
          <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-10">
            <Trans i18nKey="rulebook:attach.error.loadMore" />
          </Text>
          <Button variant={ButtonVariant.OUTLINED} type="button" onClick={onLoadMore} data-testid="RulebookAttachTargetContent-LoadMoreRetryButton">
            <Trans i18nKey="rulebook:attach.error.retry" />
          </Button>
        </div>
      ) : null}
      <RulebookAttachLoadMore
        isVisible={ownerKind === "characters" && hasMoreCharacters}
        isLoadingMore={isLoadingMoreCharacters}
        onLoadMore={onLoadMore}
      />
    </>
  );
};

export default RulebookAttachTargetContent;
